import LZString from 'lz-string';
import { Routine, TrainingDay, TrainingDayExercise, Exercise, MuscleGroup } from '../database';

// Interface for shareable routine data
export interface SharedRoutine {
  name: string;
  description?: string;
  trainingDays: SharedTrainingDay[];
  metadata: {
    sharedBy?: string;
    sharedAt: string;
    version: string;
    originalId?: number;
  };
}

export interface SharedTrainingDay {
  name: string;
  exercises: SharedExercise[];
}

export interface SharedExercise {
  name: string;
  description?: string;
  category?: string;
  primaryMuscles?: MuscleGroup[];
  secondaryMuscles?: MuscleGroup[];
  sets: { targetReps: number; order: number; }[];
  discoveryMode: true; // Always true for shared routines
  order: number;
}

/**
 * Convert a full routine from database into shareable format
 */
export async function convertRoutineToShareable(
  routine: Routine,
  trainingDays: TrainingDay[],
  allTrainingDayExercises: TrainingDayExercise[],
  allExercises: Exercise[]
): Promise<SharedRoutine> {
  const sharedTrainingDays: SharedTrainingDay[] = [];

  // Process each training day in the routine
  for (const trainingDayId of routine.trainingDayIds) {
    const trainingDay = trainingDays.find(td => td.id === trainingDayId);
    if (!trainingDay) continue;

    // Get exercises for this training day
    const dayExercises = allTrainingDayExercises
      .filter(tde => tde.trainingDayId === trainingDayId)
      .sort((a, b) => a.order - b.order);

    const sharedExercises: SharedExercise[] = [];

    for (const tde of dayExercises) {
      const exercise = allExercises.find(e => e.id === tde.exerciseId);
      if (!exercise) continue;

      sharedExercises.push({
        name: exercise.name,
        description: exercise.description,
        category: exercise.category,
        primaryMuscles: exercise.primaryMuscles,
        secondaryMuscles: exercise.secondaryMuscles,
        sets: tde.sets,
        discoveryMode: true, // Always force discovery mode for shared routines
        order: tde.order
      });
    }

    sharedTrainingDays.push({
      name: trainingDay.name,
      exercises: sharedExercises
    });
  }

  return {
    name: routine.name,
    description: `Shared routine based on "${routine.name}"`,
    trainingDays: sharedTrainingDays,
    metadata: {
      sharedAt: new Date().toISOString(),
      version: '1.0',
      originalId: routine.id
    }
  };
}

/**
 * Generate a shareable URL for a routine
 */
export function generateShareableURL(sharedRoutine: SharedRoutine, preferPWA: boolean = false): string {
  try {
    // Convert to JSON
    const json = JSON.stringify(sharedRoutine);
    
    // Compress using LZ-String
    const compressed = LZString.compressToEncodedURIComponent(json);
    
    // Create URL - use custom protocol for PWA deep linking if preferred
    let shareUrl: string;
    
    if (preferPWA) {
      // Custom protocol URL that will open in PWA
      shareUrl = `web+workouttracker://${compressed}`;
    } else {
      // Standard HTTP URL for browser sharing
      const baseUrl = window.location.origin;
      shareUrl = `${baseUrl}/import-routine?data=${compressed}`;
    }
    
    // Check URL length (most browsers support 2000+ chars)
    if (shareUrl.length > 2000) {
      console.warn('Share URL is very long:', shareUrl.length, 'characters');
    }
    
    return shareUrl;
  } catch (error) {
    console.error('Failed to generate shareable URL:', error);
    throw new Error('Failed to generate share link');
  }
}

/**
 * Parse a shared routine from URL data parameter or custom protocol
 */
export function parseSharedRoutineFromURL(dataParam: string): SharedRoutine {
  try {
    // Decompress
    const json = LZString.decompressFromEncodedURIComponent(dataParam);
    
    if (!json) {
      throw new Error('Invalid or corrupted share data');
    }
    
    // Parse JSON
    const routine = JSON.parse(json) as SharedRoutine;
    
    // Validate structure
    validateSharedRoutine(routine);
    
    return routine;
  } catch (error) {
    console.error('Failed to parse shared routine:', error);
    throw new Error('Invalid share link format');
  }
}

/**
 * Parse a shared routine from custom protocol URL
 */
export function parseSharedRoutineFromProtocol(protocolUrl: string): SharedRoutine {
  try {
    // Extract data from protocol URL: web+workouttracker://DATA
    const dataParam = protocolUrl.replace('web+workouttracker://', '');
    return parseSharedRoutineFromURL(dataParam);
  } catch (error) {
    console.error('Failed to parse protocol URL:', error);
    throw new Error('Invalid protocol link format');
  }
}

/**
 * Validate shared routine structure
 */
function validateSharedRoutine(routine: any): asserts routine is SharedRoutine {
  if (!routine || typeof routine !== 'object') {
    throw new Error('Invalid routine format');
  }
  
  if (!routine.name || typeof routine.name !== 'string') {
    throw new Error('Routine must have a name');
  }
  
  if (!Array.isArray(routine.trainingDays)) {
    throw new Error('Routine must have training days');
  }
  
  if (routine.trainingDays.length === 0) {
    throw new Error('Routine must have at least one training day');
  }
  
  // Validate each training day
  for (const day of routine.trainingDays) {
    if (!day.name || typeof day.name !== 'string') {
      throw new Error('Training days must have names');
    }
    
    if (!Array.isArray(day.exercises)) {
      throw new Error('Training days must have exercises');
    }
    
    // Validate each exercise
    for (const exercise of day.exercises) {
      if (!exercise.name || typeof exercise.name !== 'string') {
        throw new Error('Exercises must have names');
      }
      
      if (!Array.isArray(exercise.sets) || exercise.sets.length === 0) {
        throw new Error('Exercises must have at least one set');
      }
    }
  }
  
  if (!routine.metadata || !routine.metadata.version) {
    throw new Error('Routine must have metadata with version');
  }
}

/**
 * Import a shared routine into the database
 */
export async function importSharedRoutine(sharedRoutine: SharedRoutine): Promise<number> {
  const { db } = await import('../database');
  
  try {
    // Create routine
    const routineId = await db.routines.add({
      name: `${sharedRoutine.name} (Imported)`,
      trainingDayIds: [], // Will be populated after creating training days
      currentDayIndex: 0,
      active: false, // Start inactive so user can review
      createdAt: new Date(),
      updatedAt: new Date()
    });

    const trainingDayIds: number[] = [];

    // Create training days and exercises
    for (const sharedDay of sharedRoutine.trainingDays) {
      // Create training day
      const trainingDayId = await db.trainingDays.add({
        name: sharedDay.name,
        exercises: [], // Legacy field, not used
        createdAt: new Date(),
        updatedAt: new Date()
      });

      trainingDayIds.push(trainingDayId);

      // Create or find exercises and training day exercises
      for (const sharedExercise of sharedDay.exercises) {
        // Generate consistent exercise ID from name
        const { exerciseLibrary } = await import('../data/exerciseLibrary');
        const libraryExercise = exerciseLibrary.find(ex => ex.name === sharedExercise.name);
        
        let exerciseId: number;
        
        if (libraryExercise) {
          // Use consistent library ID
          exerciseId = libraryExercise.id!;
          
          // Ensure exercise exists in database with correct ID
          const existingExercise = await db.exercises.get(exerciseId);
          if (!existingExercise) {
            await db.exercises.put({
              id: exerciseId,
              name: sharedExercise.name,
              description: sharedExercise.description,
              category: sharedExercise.category,
              primaryMuscles: sharedExercise.primaryMuscles,
              secondaryMuscles: sharedExercise.secondaryMuscles,
              createdAt: new Date(),
              updatedAt: new Date()
            });
          }
        } else {
          // For custom exercises not in library, try to find by name or create new
          let customExercise = await db.exercises
            .where('name')
            .equals(sharedExercise.name)
            .first();
            
          if (!customExercise) {
            exerciseId = await db.exercises.add({
              name: sharedExercise.name,
              description: sharedExercise.description,
              category: sharedExercise.category,
              primaryMuscles: sharedExercise.primaryMuscles,
              secondaryMuscles: sharedExercise.secondaryMuscles,
              createdAt: new Date(),
              updatedAt: new Date()
            });
          } else {
            exerciseId = customExercise.id!;
          }
        }

        // Create training day exercise (only if exerciseId exists)
        if (exerciseId) {
          await db.trainingDayExercises.add({
            trainingDayId,
            exerciseId,
            weight: 0, // Start with 0 weight
            sets: sharedExercise.sets,
            order: sharedExercise.order,
            discoveryMode: true // Always start in discovery mode
          });
        }
      }
    }

    // Update routine with training day IDs
    await db.routines.update(routineId, {
      trainingDayIds,
      updatedAt: new Date()
    });

    return routineId;
  } catch (error) {
    console.error('Failed to import routine:', error);
    throw new Error('Failed to import routine into database');
  }
}

/**
 * Get routine statistics for sharing preview
 */
export function getRoutineStats(sharedRoutine: SharedRoutine) {
  const totalExercises = sharedRoutine.trainingDays.reduce(
    (total, day) => total + day.exercises.length, 
    0
  );
  
  const totalSets = sharedRoutine.trainingDays.reduce(
    (total, day) => total + day.exercises.reduce(
      (dayTotal, exercise) => dayTotal + exercise.sets.length, 
      0
    ), 
    0
  );

  const exerciseNames = new Set<string>();
  sharedRoutine.trainingDays.forEach(day => {
    day.exercises.forEach(exercise => {
      exerciseNames.add(exercise.name);
    });
  });

  return {
    trainingDays: sharedRoutine.trainingDays.length,
    totalExercises,
    uniqueExercises: exerciseNames.size,
    totalSets,
    estimatedDuration: Math.round(totalSets * 2.5) // Rough estimate: 2.5 min per set
  };
}